import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { StorageService } from './storage.service';
import { AudioService } from './audio.service';
import { STORAGE_KEYS } from '../constants/storage.constants';
import { DebugLoggerService } from './debug-logger.service';
import { ServerSyncService } from './server-sync.service';
import { TestModeService } from './test-mode.service';
import { DeviceService } from './device.service';
import { WebSocketSignalingService } from './websocket-signaling.service';

export interface TicketRecord {
  ticketId: string;
  timestamp: number;
  isPresent: boolean;
  eventId: string;
  syncSource?: 'local' | 'remote';
  originDeviceId?: string;
}

// Add a new event for ticket updates
export const TICKET_UPDATED = new Subject<TicketRecord>();

@Injectable({
  providedIn: 'root'
})
export class AttendanceService {
  // NEW: Primary signal-based state
  private readonly countSignal = signal<number>(0);
  private readonly deviceCountSignal = signal<number>(0);
  private readonly currentEventIdSignal = signal<string | null>(null);

  // NEW: Public signals
  public readonly count = this.countSignal;
  public readonly deviceCount = this.deviceCountSignal.asReadonly();
  public readonly currentEventId = this.currentEventIdSignal.asReadonly();

  // NEW: Computed signals for derived state
  public readonly hasAttendees = computed(() => this.count() > 0);

  // BACKWARD COMPATIBILITY: Keep observable for gradual migration
  private countSubject = new BehaviorSubject<number>(0);
  public count$ = this.countSubject.asObservable();

  // IndexedDB setup
  private db: IDBDatabase | null = null;
  private readonly DB_NAME = 'attendance_db';
  private readonly TICKET_STORE = 'tickets';
  private readonly DB_VERSION = 1;

  // Testing and development overrides
  private dateValidationEnabled = true;
  private testingMode = false;

  private offers = new Map<string, any>();

  private serverSyncService = inject(ServerSyncService);
  private deviceService = inject(DeviceService);
  private signaling = inject(WebSocketSignalingService);
  private webrtcMeshService?: any; // Lazy inject to avoid circular dependency
  private testModeService = inject(TestModeService);

  constructor(
    private storage: StorageService,
    private audioService: AudioService,
    private logger: DebugLoggerService
  ) {
    (window as any).__testAttendanceService = this;

    // Sync signal with legacy observable during transition
    effect(() => {
      this.countSubject.next(this.count());
    });

    this.initDb().catch(err => {
      console.error('[Attendance] Failed to initialize database:', err);
    });

    // Check if we're in development/testing mode
    this.checkTestingMode();
  }

  /**
   * Check if we're in testing mode and set appropriate overrides
   */
  private checkTestingMode() {
    // Check for testing indicators
    const isLocalhost = window.location.hostname === 'localhost';
    const isTestEnv = window.location.hostname.includes('test');
    const hasTestParam = window.location.search.includes('test=true');
    const isDevelopment = !window.location.protocol.startsWith('https') || isLocalhost;

    this.testingMode = isDevelopment || isTestEnv || hasTestParam;

    if (this.testingMode) {
      this.logger.log('Attendance', '🧪 Testing mode detected - validation overrides available', {
        hostname: window.location.hostname,
        protocol: window.location.protocol,
        isDevelopment,
        isTestEnv,
        hasTestParam
      });
    }
  }

  /**
   * Enable or disable date validation (for testing purposes)
   * @param enabled Whether date validation should be enforced
   */
  public setDateValidationEnabled(enabled: boolean): void {
    if (this.testingMode) {
      this.dateValidationEnabled = enabled;
      this.logger.log('Attendance', `🧪 Date validation ${enabled ? 'ENABLED' : 'DISABLED'} for testing`);
    } else {
      this.logger.warn('Attendance', 'Date validation override only available in testing mode');
    }
  }

  /**
   * Check if we're currently in testing mode
   */
  public isTestingMode(): boolean {
    return this.testingMode;
  }

  /**
   * Get current validation settings
   */
  public getValidationSettings(): { dateValidationEnabled: boolean; testingMode: boolean } {
    return {
      dateValidationEnabled: this.dateValidationEnabled,
      testingMode: this.testingMode
    };
  }

  private async initDb() {
    try {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error('[Attendance] Database error:', request.error);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.TICKET_STORE)) {
          const store = db.createObjectStore(this.TICKET_STORE, { keyPath: 'ticketId' });
          store.createIndex('eventId', 'eventId', { unique: false });
          store.createIndex('isPresent', 'isPresent', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
      };
    } catch (error) {
      console.error('[Attendance] Failed to initialize database:', error);
    }
  }

  async processTickets(tickets: TicketRecord[], isRemote: boolean = false): Promise<void> {
    if (isRemote) {

      const db = await this.getDb(); // Get DB instance once
      const transaction = db.transaction([this.TICKET_STORE], 'readwrite');
      const store = transaction.objectStore(this.TICKET_STORE);
      const promises: Promise<void>[] = [];

      for (const ticket of tickets) {
        promises.push(new Promise((resolve, reject) => {
          const request = store.put({
            ...ticket,
            syncSource: 'remote'
          }); // Use put for upsert
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        }));
      }
      await Promise.all(promises); // Wait for all DB operations to complete
      if (tickets.length > 0) {
        const eventId = tickets[0].eventId; // All tickets in a sync should be for the same event
        const recalculatedTotal = await this.getTotalScanned(eventId);

        await this.storage.set(this.getStorageKey(eventId), recalculatedTotal.toString());

        if (this.currentEventIdSignal() === eventId || !this.currentEventIdSignal()) {
          this.countSignal.set(recalculatedTotal);
          this.countSubject.next(recalculatedTotal);
        }
      }
    } else {
      for (const ticket of tickets) {
        if (ticket.isPresent) {
          await this.processAttendee(ticket.ticketId, ticket.eventId, 'local', ticket.originDeviceId);
        } else {
          await this.processExit(ticket.ticketId, ticket.eventId, false, 'local', ticket.originDeviceId);
        }
      }
    }
  }

  // Helper method to update total count safely
  private async _updateTotalCount(eventId: string, delta: number): Promise<void> {
    const currentCount = await this.getCount(eventId);
    let newCount = currentCount + delta;

    // Ensure total count never goes below zero
    if (newCount < 0) {
      newCount = 0;
      this.logger.warn('Attendance', `Attempted to decrement total count below zero for event ${eventId}. Capped at 0.`);
    }



    if (this.currentEventIdSignal() === eventId || !this.currentEventIdSignal()) {
      this.countSignal.set(newCount);
    }

    await this.storage.set(this.getStorageKey(eventId), newCount.toString());
    this.logger.log('Attendance', `Total count updated for event ${eventId} to ${newCount} (delta: ${delta})`);
  }

  async incrementCount(eventId: string, eventName?: string, eventPassword?: string, isCreator?: boolean): Promise<number> {
    try {
      const deviceCount = await this.incrementDeviceCount(eventId);
      queueMicrotask(() => {
        void this.audioService.playInSound();
      });

      this.pushManualCountToServer(eventId, deviceCount, deviceCount, eventName, eventPassword, isCreator);
      return this.deviceCountSignal();
    } catch (error) {
      this.logger.error('Attendance', `Error incrementing count for event ${eventId}:`, error);
      throw error;
    }
  }

  public async decrementCount(eventId: string, eventName?: string, eventPassword?: string, isCreator?: boolean): Promise<void> {
    try {
      // For MANUAL events: Only update device count (total is computed from all device counts)
      // For QR events: Update total count (from tickets)
      const deviceCount = await this.decrementDeviceCount(eventId);
      queueMicrotask(() => {
        void this.audioService.playOutSound();
      });

      this.pushManualCountToServer(eventId, deviceCount, deviceCount, eventName, eventPassword, isCreator);
      
      return;
    } catch (error) {
      this.logger.error('Attendance', `Error decrementing count for event ${eventId}:`, error);
      throw error;
    }
  }

  public publishManualCount(
    eventId: string,
    deviceCount: number,
    totalCount: number,
    eventName?: string,
    eventPassword?: string,
    isCreator?: boolean
  ): void {
    this.pushManualCountToServer(eventId, deviceCount, totalCount, eventName, eventPassword, isCreator);
  }

  async applyServerManualTotal(eventId: string, total: number): Promise<void> {
    if (!eventId || !Number.isFinite(total) || total < 0) {
      return;
    }
    this.countSignal.set(total);
    await this.storage.set(this.getStorageKey(eventId), total.toString());
  }

  private pushManualCountToServer(
    eventId: string,
    deviceCount: number,
    totalCount: number,
    eventName?: string,
    eventPassword?: string,
    isCreator?: boolean
  ): void {
    const deviceId = this.deviceService.deviceId();
    if (deviceId) {
      void this.deviceService.getDeviceInfo().then(info => {
        this.signaling.sendAttendanceUpdate({
          eventId,
          deviceId,
          deviceCount,
          totalCount,
          deviceName: info.userDisplayName || info.deviceName,
          platform: info.platform,
          userDisplayName: info.userDisplayName || info.deviceName
        });
      }).catch(() => {
        this.signaling.sendAttendanceUpdate({
          eventId,
          deviceId,
          deviceCount,
          totalCount,
          deviceName: this.deviceService.userDisplayName() || undefined,
          userDisplayName: this.deviceService.userDisplayName() || undefined
        });
      });
    }

    this.serverSyncService.syncDeviceCount(eventId, deviceCount, totalCount, eventName, eventPassword, isCreator).catch(err => {
      this.logger.log('Attendance', 'Count sync queued for later (offline)', err);
    });
  }

  public async processAttendee(code: string, eventId: string, syncSource: 'local' | 'remote' = 'local', originDeviceId?: string): Promise<boolean> {
    try {
      const ticket = await this.getTicketFromDb(code);

      if (ticket && ticket.isPresent) {
        this.logger.log('Attendance', 'Ticket already processed as entry:', code);
        await this.audioService.playErrorSound();
        return false; // Already present
      }

      await this.saveTicketToDb({
        ticketId: code,
        timestamp: Date.now(),
        isPresent: true,
        eventId: eventId,
        syncSource,
        ...(originDeviceId ? { originDeviceId } : {})
      });
      // Audio sound is handled by processLocalScan or webrtc-mesh.service for remote updates

      // Only update total count, device count handled by processLocalScan or ignored for remote
      await this._updateTotalCount(eventId, 1);

      return true;
    } catch (error) {
      this.logger.error('Attendance', 'Failed to process attendee:', error);
      await this.audioService.playErrorSound();
      return false;
    }
  }

  public async processExit(code: string, eventId: string, forceUpdate: boolean = false, syncSource: 'local' | 'remote' = 'local', originDeviceId?: string): Promise<boolean> {
    try {
      const ticket = await this.getTicketFromDb(code);

      if (!forceUpdate && (!ticket || !ticket.isPresent)) {
        this.logger.log('Attendance', 'Ticket not found or already exited (not forced):', code);
        await this.audioService.playErrorSound(); // Play error sound for local scan
        return false;
      }

      // If forceUpdate is true, or if ticket is found and present, proceed
      if (ticket && ticket.isPresent) {
        // Only play sound and decrement if the ticket was previously present
        // Audio sound is handled by processLocalScan or webrtc-mesh.service for remote updates
        await this._updateTotalCount(eventId, -1);
      } else if (forceUpdate && ticket && !ticket.isPresent) {
        // If forced and ticket already exited, still log, but don't decrement total count again
        // The decrement will have happened on the scanning device, this is just to sync state
        this.logger.log('Attendance', 'Forcing exit update for already exited ticket (state sync - no total count change):', code);
      } else if (forceUpdate && !ticket) {
        // If forced and ticket not found, it's a new exit for this device, so decrement total count
        // Audio sound is handled by processLocalScan or webrtc-mesh.service for remote updates
        await this._updateTotalCount(eventId, -1);
      }

      await this.saveTicketToDb({
        ticketId: code,
        timestamp: Date.now(),
        isPresent: false,
        eventId: eventId,
        syncSource,
        ...(originDeviceId ? { originDeviceId } : {})
      });

      return true;
    } catch (error) {
      this.logger.error('Attendance', 'Failed to process exit:', error);
      await this.audioService.playErrorSound();
      return false;
    }
  }

  private isTestModeEnabledForEvent(eventId: string): boolean {
    return this.testModeService.isTestModeEnabled(eventId);
  }

  /**
   * Process a remote attendance update (from WebRTC peer) and update total count and ticket presence.
   * This method should be called by the WebrtcMeshService.
   * It DOES NOT update deviceCount.
   */
  public async processRemoteAttendanceUpdate(ticketId: string, eventId: string, action: 'scan_in' | 'scan_out', originDeviceId?: string): Promise<boolean> {
    this.logger.log('Attendance', `[Remote Update] Processing ${action} for ticket:`, ticketId);

    try {
      const ticket = await this.getTicketFromDb(ticketId);

      if (action === 'scan_in') {
        if (ticket && ticket.isPresent) {
          this.logger.log('Attendance', '[Remote Update] Ticket already marked as present, skipping entry update:', ticketId);
          return false; // Already present
        }
        await this.saveTicketToDb({
          ticketId: ticketId,
          timestamp: Date.now(),
          isPresent: true,
          eventId: eventId,
          syncSource: 'remote',
          ...(originDeviceId ? { originDeviceId } : {})
        });
        await this._updateTotalCount(eventId, 1);
        this.logger.log('Attendance', '[Remote Update] Entry recorded:', ticketId);
        return true;
      } else { // action === 'scan_out'
        // For remote exit updates, we force the update so that total count is correctly adjusted
        // and the ticket is marked as exited locally, even if it wasn't previously present
        // (e.g., if this device didn't scan the entry initially).
        const success = await this.processExit(ticketId, eventId, true, 'remote', originDeviceId);
        if (success) {
          this.logger.log('Attendance', '[Remote Update] Exit recorded:', ticketId);
        } else {
          this.logger.warn('Attendance', '[Remote Update] Failed to record exit for ticket:', ticketId);
        }
        return success;
      }
    } catch (error) {
      this.logger.error('Attendance', `[Remote Update] Error processing remote attendance update for ${action} ${ticketId}:`, error);
      return false;
    }
  }

  /**
   * Process a local scan (entry or exit) and update both total and device counts.
   * This method should be called by the QR scanner component.
   */
  public async processLocalScan(code: string, eventId: string, action: 'scan_in' | 'scan_out', eventPassword?: string, isCreator?: boolean): Promise<boolean> {
    let ticketId = code;
    const existingTicket = await this.getTicketFromDb(code);
    let success = false;
    const minimumExitIntervalMs = 15 * 1000;
    const localDeviceId = this.deviceService.deviceId() || undefined;

    this.logger.log('Attendance', `[Local Scan] Processing ${action} for ticket:`, code);

    if (action === 'scan_in') {
      if (existingTicket && existingTicket.isPresent) {
        this.logger.log('Attendance', `[Local Scan] Ticket already present for entry: ${code}`, existingTicket);
        await this.audioService.playErrorSound();
        return false;
      }
      await this.incrementDeviceCount(eventId);
      this.logger.log('Attendance', `[Local Scan] Device count incremented for entry:`, code);
      success = await this.processAttendee(ticketId, eventId, 'local', localDeviceId);
      if (success) {
        await this.audioService.playInSound();
      }

    } else { // action === 'scan_out'
      if (!existingTicket) {
        this.logger.log('Attendance', `[Local Scan] Ticket has no recorded entry for exit:`, code);
        await this.audioService.playErrorSound();
        return false;
      }

      if (!existingTicket.isPresent) {
        this.logger.log('Attendance', `[Local Scan] Ticket already exited: ${code}`, existingTicket);
        await this.audioService.playErrorSound();
        return false;
      }

      if ((Date.now() - existingTicket.timestamp) < minimumExitIntervalMs) {
        this.logger.log('Attendance', `[Local Scan] Ticket exit attempted too soon after entry: ${code}`, {
          entryTimestamp: existingTicket.timestamp,
          minimumExitIntervalMs
        });
        await this.audioService.playErrorSound();
        return false;
      }

      await this.decrementDeviceCount(eventId);
      this.logger.log('Attendance', `[Local Scan] Device count decremented for exit:`, code);
      success = await this.processExit(ticketId, eventId, false, 'local', localDeviceId);
      if (success) {
        await this.audioService.playOutSound();
      }
    }
    
    // Background server sync for QR tickets and fetch accurate count
    if (success) {
      const localPresentCount = await this.getTotalScanned(eventId);
      this.serverSyncService.syncTicketScans(eventId, [{
        ticketId: ticketId,
        action,
        timestamp: Date.now()
      }], localPresentCount, undefined, eventPassword, isCreator, false).then(async (syncResult) => {
        const serverCount = await this.serverSyncService.getServerCount(eventId);
        if (serverCount !== null) {
          const localCount = this.countSignal();
          if (serverCount !== localCount) {
            this.logger.log('Attendance', `Server count (${serverCount}) differs from local (${localCount}), updating...`);
            this.countSignal.set(serverCount);
            this.countSubject.next(serverCount);
            
            await this.storage.set(`count_${eventId}`, serverCount.toString());
          }
          
          this.notifyPeersOfServerUpdate(eventId, serverCount);
        }
      }).catch(err => {
        this.logger.log('Attendance', 'Ticket sync queued for later (offline)', err);
      });
    }
    
    return success;
  }

  public async processRemoteManualCountUpdate(eventId: string, action: 'increment' | 'decrement'): Promise<void> {
    let currentTotalCount = await this.getTotalCount(eventId);
    let newTotalCount = currentTotalCount;

    if (action === 'increment') {
      newTotalCount++;
    } else if (action === 'decrement') {
      newTotalCount = Math.max(0, newTotalCount - 1);
    }

    await this.storage.set(this.getStorageKey(eventId), newTotalCount.toString());
    if (this.currentEventIdSignal() === eventId) {
      this.countSignal.set(newTotalCount);
      this.countSubject.next(newTotalCount);
    }
    this.logger.log('Attendance', `Processed remote manual count update for event ${eventId}: ${action}, new total: ${newTotalCount}`);
  }

  public async processRemoteTicket(ticketId: string, eventId: string): Promise<boolean> {
    try {
      const ticket = await this.getTicketFromDb(ticketId);

      if (ticket && ticket.isPresent) {
        this.logger.log('Attendance', '[Remote Ticket] Ticket already present, skipping entry update:', ticketId);
        return false; // Already present
      }

      await this.saveTicketToDb({
        ticketId: ticketId,
        timestamp: Date.now(),
        isPresent: true,
        eventId: eventId,
        syncSource: 'remote'
      });

      await this._updateTotalCount(eventId, 1);
      this.logger.log('Attendance', '[Remote Ticket] Entry recorded:', ticketId);
      return true;
    } catch (error) {
      this.logger.error('Attendance', `[Remote Ticket] Error processing remote ticket for ${ticketId}:`, error);
      return false;
    }
  }

  public async processRemoteTicketExit(ticketId: string, eventId: string): Promise<boolean> {
    try {
      const ticket = await this.getTicketFromDb(ticketId);

      // If the ticket isn't found or already marked as exited, we still update total count for sync purposes
      // and save the ticket state, but log a warning.
      if (!ticket || ticket.isPresent === false) {
        this.logger.warn('Attendance', '[Remote Ticket Exit] Ticket not found or already exited locally, but processing remote exit for total count sync:', ticketId);
      }

      await this.saveTicketToDb({
        ticketId: ticketId,
        timestamp: Date.now(),
        isPresent: false,
        eventId: eventId,
        syncSource: 'remote'
      });

      await this._updateTotalCount(eventId, -1);
      this.logger.log('Attendance', '[Remote Ticket Exit] Exit recorded:', ticketId);
      return true;
    } catch (error) {
      this.logger.error('Attendance', `[Remote Ticket Exit] Error processing remote ticket exit for ${ticketId}:`, error);
      return false;
    }
  }

  /**
   * Directly updates the total count for an event from a remote source.
   * This method ensures the total count is synchronized without affecting the local deviceCount.
   * @param eventId The ID of the event.
   * @param newTotalCount The new total count received from a remote peer.
   */
  public async processRemoteTotalCountUpdate(eventId: string, newTotalCount: number): Promise<void> {
    this.logger.log('Attendance', `[Remote Update] Processing total count update for event: ${eventId}, new total: ${newTotalCount}`);
    try {
      if (newTotalCount < 0) {
        newTotalCount = 0;
        this.logger.warn('Attendance', `Attempted to set total count below zero for event ${eventId}. Capped at 0.`);
      }
      await this.storage.set(this.getStorageKey(eventId), newTotalCount.toString());
      if (this.currentEventIdSignal() === eventId) {
        this.countSignal.set(newTotalCount);
        this.countSubject.next(newTotalCount);
      }
      this.logger.log('Attendance', `Total count set for event ${eventId} to ${newTotalCount} (from remote update)`);
    } catch (error) {
      this.logger.error('Attendance', `[Remote Update] Error processing remote total count update for event ${eventId}:`, error);
      throw error;
    }
  }

  public async getTicketFromDb(ticketId: string): Promise<TicketRecord | undefined> {
    return this.getTicketFromDbInternal(ticketId);
  }

  public async saveTicketToDb(ticket: TicketRecord): Promise<void> {
    await this.saveTicketToDbInternal(ticket);
    // Emit an event that a ticket has been updated
    // This will allow other services to react to ticket changes
    TICKET_UPDATED.next(ticket);
  }

  private getTicketFromDbInternal(ticketId: string): Promise<TicketRecord | undefined> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.TICKET_STORE], 'readonly');
      const store = transaction.objectStore(this.TICKET_STORE);
      const request = store.get(ticketId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  private saveTicketToDbInternal(ticket: TicketRecord): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.TICKET_STORE], 'readwrite');
      const store = transaction.objectStore(this.TICKET_STORE);
      const request = store.put(ticket);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private getStorageKey(eventId: string): string {
    return `count_${eventId}`;
  }

  private getDeviceCountKey(eventId: string): string {
    return `device_count_${eventId}`;
  }

  async getCount(eventId: string): Promise<number> {
    try {
      const key = this.getStorageKey(eventId);
      const count = await this.storage.get(key);
      const newCount = count ? parseInt(count, 10) : 0;
      if (this.currentEventIdSignal() === eventId || !this.currentEventIdSignal()) {
        this.countSignal.set(newCount);
        this.countSubject.next(newCount);
      }
      return newCount;
    } catch (error) {
      console.error('[Attendance] Error getting count:', error);
      return 0;
    }
  }

  async getDeviceCount(eventId: string): Promise<number> {
    try {
      const deviceCountStr = await this.storage.get(this.getDeviceCountKey(eventId));
      const deviceCount = deviceCountStr ? parseInt(deviceCountStr, 10) : 0;
      // console.log(`[AttendanceService] Device count for event ${eventId}:`, deviceCount);
      return deviceCount;
    } catch (error) {
      console.error('[AttendanceService] Failed to get device count:', error);
      return 0;
    }
  }

  /**
   * Get total count for an event (alias to getCount for audit compatibility)
   */
  async getTotalCount(eventId: string): Promise<number> {
    return this.getCount(eventId);
  }

  async incrementDeviceCount(eventId: string): Promise<number> {
    const currentCount = this.deviceCountSignal();
    const newCount = currentCount + 1;
    this.deviceCountSignal.set(newCount);
    void this.storage.set(this.getDeviceCountKey(eventId), newCount.toString());
    this.logger.log('Attendance', `[Attendance] Device count incremented:`, { eventId, currentCount, newCount });
    return newCount;
  }

  async decrementDeviceCount(eventId: string): Promise<number> {
    const currentCount = this.deviceCountSignal();
    // Device counts may go negative (e.g. exit-only partner phone). The shared/general total is floored separately.
    const newCount = currentCount - 1;
    this.deviceCountSignal.set(newCount);
    void this.storage.set(this.getDeviceCountKey(eventId), newCount.toString());
    this.logger.log('Attendance', `[Attendance] Device count decremented:`, { eventId, currentCount, newCount });
    return newCount;
  }

  public async getTotalScanned(eventId: string): Promise<number> {
    if (!this.db) {
      console.error('[Attendance] Database not initialized');
      return 0;
    }

    try {
      const transaction = this.db.transaction([this.TICKET_STORE], 'readonly');
      const store = transaction.objectStore(this.TICKET_STORE);
      const index = store.index('eventId');
      const request = index.getAll(IDBKeyRange.only(eventId));

      return new Promise((resolve) => {
        request.onsuccess = () => {
          const tickets: TicketRecord[] = request.result || [];
          // Count only tickets with isPresent: true
          const presentCount = tickets.filter(t => t.isPresent).length;
          resolve(presentCount);
        };
        request.onerror = () => {
          console.error('[Attendance] Error reading tickets:', request.error);
          resolve(0);
        };
      });
    } catch (error) {
      console.error('[Attendance] Error getting total scanned:', error);
      return 0;
    }
  }

  /**
   * Download the latest ticket states from the server and update local IDB.
   * Returns true if the update was applied, false if the fetch failed.
   */
  public async syncTicketStatesFromServer(eventId: string): Promise<boolean> {
    try {
      const serverResponse = await this.serverSyncService.getEventTickets(eventId);
      if (!serverResponse?.success || !serverResponse.tickets) {
        return false;
      }

      let presentCount = 0;

      for (const serverTicket of serverResponse.tickets) {
        const isPresent = serverTicket.status === 'present';
        if (isPresent) presentCount++;

        await this.saveTicketToDbInternal({
          ticketId: serverTicket.ticketId,
          timestamp: serverTicket.timestamp,
          isPresent,
          eventId,
          syncSource: 'remote'
        });
      }

      this.countSignal.set(presentCount);
      this.countSubject.next(presentCount);
      await this.storage.set(this.getStorageKey(eventId), presentCount.toString());

      this.logger.log('Attendance', `Synced ${serverResponse.tickets.length} ticket states from server (${presentCount} present)`);
      return true;
    } catch (error) {
      this.logger.error('Attendance', 'Failed to sync ticket states from server:', error);
      return false;
    }
  }

  public async getCurrentlyPresent(eventId: string): Promise<number> {
    if (!this.db) {
      console.error('[Attendance] Database not initialized');
      return 0;
    }

    try {
      const transaction = this.db.transaction([this.TICKET_STORE], 'readonly');
      const store = transaction.objectStore(this.TICKET_STORE);
      const index = store.index('eventId');
      const request = index.openCursor(IDBKeyRange.only(eventId));

      return new Promise((resolve) => {
        let count = 0;
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            if (cursor.value.isPresent) {
              count++;
            }
            cursor.continue();
          } else {
            resolve(count);
          }
        };
      });
    } catch (error) {
      console.error('[Attendance] Error getting currently present:', error);
      return 0;
    }
  }

  async storeOffer(tagId: string, offer: any): Promise<void> {
    this.offers.set(tagId, offer);
  }

  async getOffer(tagId: string): Promise<any> {
    const offer = this.offers.get(tagId);
    if (!offer) {
      throw new Error('No offer found for tag ID');
    }
    return offer;
  }

  async refreshStats() {
    // Reload data from storage
    await this.loadStats();
    // loadStats already sets both count and device count signals
  }

  private async loadStats(): Promise<void> {
    const eventId = await this.storage.get(STORAGE_KEYS.CURRENT_EVENT);
    if (!eventId) return;
    this.currentEventIdSignal.set(eventId);

    const count = await this.getCount(eventId);
    const deviceCount = await this.getDeviceCount(eventId);



    this.countSignal.set(count);
    this.deviceCountSignal.set(deviceCount);

  }

  async switchEvent(eventId: string, countMethod?: string): Promise<void> {
    this.currentEventIdSignal.set(eventId);
    const deviceCount = await this.getDeviceCount(eventId);
    let totalCount = 0;

    if (countMethod === 'manual') {
      totalCount = await this.getCount(eventId);
    } else {
      // QR events: recalculate from IndexedDB to ensure accuracy
      totalCount = await this.getTotalScanned(eventId);
      await this.storage.set(`count_${eventId}`, totalCount.toString());
    }

    // Update signals
    this.countSignal.set(totalCount);
    this.deviceCountSignal.set(deviceCount);
  }

  /**
   * Get all tickets for a specific event
   * @param eventId The ID of the event to get tickets for
   * @returns Promise resolving to an array of ticket records
   */
  public async getTicketsForEvent(eventId: string): Promise<TicketRecord[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      try {
        const transaction = this.db.transaction([this.TICKET_STORE], 'readonly');
        const store = transaction.objectStore(this.TICKET_STORE);
        const index = store.index('eventId');
        const request = index.getAll(eventId);

        request.onsuccess = () => {
          resolve(request.result || []);
        };

        request.onerror = () => {
          console.error('[Attendance] Failed to get tickets for event:', request.error);
          reject(request.error);
        };
      } catch (error) {
        console.error('[Attendance] Failed to get tickets for event:', error);
        reject(error);
      }
    });
  }

  /**
   * Delete all tickets for a specific event from IndexedDB
   * @param eventId The ID of the event to delete tickets for
   * @returns Promise resolving to the number of tickets deleted
   */
  public async deleteTicketsForEvent(eventId: string): Promise<number> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      this.logger.log('Attendance', `Deleting all tickets for event: ${eventId}`);

      const transaction = this.db.transaction([this.TICKET_STORE], 'readwrite');
      const store = transaction.objectStore(this.TICKET_STORE);
      const index = store.index('eventId');

      let deletedCount = 0;

      // First, get all tickets for this event
      const getAllRequest = index.getAll(eventId);

      getAllRequest.onsuccess = () => {
        const tickets = getAllRequest.result || [];
        this.logger.log('Attendance', `Found ${tickets.length} tickets to delete for event ${eventId}`);

        if (tickets.length === 0) {
          resolve(0);
          return;
        }

        // Delete each ticket
        let pendingDeletes = tickets.length;

        tickets.forEach(ticket => {
          const deleteRequest = store.delete(ticket.ticketId);

          deleteRequest.onsuccess = () => {
            deletedCount++;
            pendingDeletes--;

            if (pendingDeletes === 0) {
              this.logger.log('Attendance', `✅ Deleted ${deletedCount} tickets for event ${eventId}`);
              resolve(deletedCount);
            }
          };

          deleteRequest.onerror = () => {
            this.logger.error('Attendance', `Failed to delete ticket ${ticket.ticketId}:`, deleteRequest.error);
            pendingDeletes--;

            if (pendingDeletes === 0) {
              resolve(deletedCount);
            }
          };
        });
      };

      getAllRequest.onerror = () => {
        this.logger.error('Attendance', 'Failed to get tickets for deletion:', getAllRequest.error);
        reject(getAllRequest.error);
      };

      transaction.onerror = () => {
        this.logger.error('Attendance', 'Transaction failed during ticket deletion:', transaction.error);
        reject(transaction.error);
      };
    });
  }

  /**
   * Clean up orphaned tickets that belong to deleted events
   * @param validEventIds Array of event IDs that should be kept
   * @returns Promise resolving to the number of orphaned tickets deleted
   */
  public async cleanupOrphanedTickets(validEventIds: string[]): Promise<number> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      this.logger.log('Attendance', `Cleaning up orphaned tickets. Valid events: ${validEventIds.length}`);

      const transaction = this.db.transaction([this.TICKET_STORE], 'readwrite');
      const store = transaction.objectStore(this.TICKET_STORE);

      let deletedCount = 0;

      // Get all tickets
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = () => {
        const allTickets = getAllRequest.result || [];
        this.logger.log('Attendance', `Found ${allTickets.length} total tickets in database`);

        // Find orphaned tickets (tickets for events that no longer exist)
        const orphanedTickets = allTickets.filter(ticket =>
          !validEventIds.includes(ticket.eventId)
        );

        this.logger.log('Attendance', `Found ${orphanedTickets.length} orphaned tickets to delete`);

        if (orphanedTickets.length === 0) {
          resolve(0);
          return;
        }

        // Delete each orphaned ticket
        let pendingDeletes = orphanedTickets.length;

        orphanedTickets.forEach(ticket => {
          const deleteRequest = store.delete(ticket.ticketId);

          deleteRequest.onsuccess = () => {
            deletedCount++;
            pendingDeletes--;

            this.logger.log('Attendance', `Deleted orphaned ticket: ${ticket.ticketId} (event: ${ticket.eventId})`);

            if (pendingDeletes === 0) {
              this.logger.log('Attendance', `✅ Cleanup complete: deleted ${deletedCount} orphaned tickets`);
              resolve(deletedCount);
            }
          };

          deleteRequest.onerror = () => {
            this.logger.error('Attendance', `Failed to delete orphaned ticket ${ticket.ticketId}:`, deleteRequest.error);
            pendingDeletes--;

            if (pendingDeletes === 0) {
              resolve(deletedCount);
            }
          };
        });
      };

      getAllRequest.onerror = () => {
        this.logger.error('Attendance', 'Failed to get all tickets for cleanup:', getAllRequest.error);
        reject(getAllRequest.error);
      };

      transaction.onerror = () => {
        this.logger.error('Attendance', 'Transaction failed during orphaned ticket cleanup:', transaction.error);
        reject(transaction.error);
      };
    });
  }

  /**
   * Request ticket synchronization from peers when count mismatch is detected
   * @param eventId The event ID to request tickets for
   * @param expectedCount The count reported by peer
   * @param actualLocalCount The local ticket count
   * @returns Promise that resolves when sync request is sent
   */
  public async requestTicketSync(eventId: string, expectedCount: number, actualLocalCount: number): Promise<void> {
    this.logger.log('Attendance', '🔄 Requesting ticket sync due to count mismatch', {
      eventId,
      expectedCount,
      actualLocalCount,
      deficit: expectedCount - actualLocalCount
    });

    // This will be called by the WebRTC service to broadcast sync request
    // For now, we'll emit an event that other services can listen to
    const syncRequest = {
      type: 'ticket_sync_request',
      eventId,
      expectedCount,
      actualLocalCount,
      timestamp: Date.now(),
      requesterId: 'local_device' // Will be replaced with actual device ID
    };

    // Store sync request for processing
    this.logger.log('Attendance', '📤 Ticket sync request prepared', syncRequest);
  }

  /**
   * Validate if a ticket belongs to the correct event (name + date validation)
   * @param ticketData Ticket information from QR code or peer
   * @param eventName Expected event name
   * @param eventDate Expected event date
   * @returns boolean indicating if ticket is valid for this event
   */
  public validateTicketEventMatch(ticketData: any, eventName: string, eventDate: Date): boolean {
    // For multi-day events, check both name and date
    if (ticketData.eventName && ticketData.eventDate) {
      const ticketDate = new Date(ticketData.eventDate);
      const isSameDay = this.dateValidationEnabled ?
        ticketDate.toDateString() === eventDate.toDateString() :
        true; // Skip date check in testing mode
      const isSameName = ticketData.eventName.toLowerCase().trim() === eventName.toLowerCase().trim();

      this.logger.log('Attendance', '🎫 Validating ticket event match', {
        ticketName: ticketData.eventName,
        expectedName: eventName,
        ticketDate: ticketDate.toISOString(),
        expectedDate: eventDate.toISOString(),
        isSameName,
        isSameDay,
        dateValidationEnabled: this.dateValidationEnabled,
        testingMode: this.testingMode,
        isValid: isSameName && isSameDay
      });

      if (!this.dateValidationEnabled && !isSameDay) {
        this.logger.log('Attendance', '🧪 Date validation bypassed for testing', {
          ticketDate: ticketDate.toDateString(),
          eventDate: eventDate.toDateString()
        });
      }

      return isSameName && isSameDay;
    }

    // Fallback for tickets without date info (legacy support)
    if (ticketData.eventName) {
      const isSameName = ticketData.eventName.toLowerCase().trim() === eventName.toLowerCase().trim();
      this.logger.log('Attendance', '🎫 Validating ticket name only (legacy)', {
        ticketName: ticketData.eventName,
        expectedName: eventName,
        isSameName,
        testingMode: this.testingMode
      });
      return isSameName;
    }

    this.logger.warn('Attendance', '⚠️ Ticket missing event information', { ticketData });
    return false;
  }

  /**
   * Detect potential ticket fraud by analyzing timing and patterns
   * @param ticketId The ticket ID to analyze
   * @param newTimestamp The timestamp of the new scan attempt
   * @param currentEvent Optional current event to check fraud detection settings
   * @returns Object with fraud analysis results
   */
  public async detectTicketFraud(ticketId: string, intendedAction: 'scan_in' | 'scan_out', newTimestamp: number, currentEvent?: any): Promise<{
    isSuspicious: boolean;
    reason: string;
    confidence: 'low' | 'medium' | 'high';
    allowOverride: boolean;
    existingTicket?: TicketRecord;
  }> {
    // Check if fraud detection is disabled for this event
    if (currentEvent && currentEvent.enableFraudDetection === false) {
      this.logger.log('Attendance', '🔓 Fraud detection disabled for this event', {
        eventId: currentEvent.id,
        eventName: currentEvent.name,
        ticketId
      });

      return {
        isSuspicious: false,
        reason: 'Fraud detection disabled for this event',
        confidence: 'low',
        allowOverride: true
      };
    }

    // Also skip fraud detection in testing mode
    if (this.testingMode) {
      this.logger.log('Attendance', '🧪 Fraud detection bypassed in testing mode', {
        ticketId,
        testingMode: this.testingMode
      });

      return {
        isSuspicious: false,
        reason: 'Testing mode - fraud detection bypassed',
        confidence: 'low',
        allowOverride: true
      };
    }

    const existingTicket = await this.getTicketFromDb(ticketId);
    const minimumExitIntervalMs = 15 * 1000;

    if (intendedAction === 'scan_in' && !existingTicket) {
      return {
        isSuspicious: false,
        reason: 'New ticket - no previous record',
        confidence: 'low',
        allowOverride: true
      };
    }

    if (intendedAction === 'scan_in' && existingTicket && existingTicket.isPresent) {
      return {
        isSuspicious: true,
        reason: 'This ticket has already been scanned for entry.',
        confidence: 'high',
        allowOverride: false,
        existingTicket
      };
    }

    if (intendedAction === 'scan_in') {
      return {
        isSuspicious: false,
        reason: 'Ticket can re-enter after a recorded exit.',
        confidence: 'low',
        allowOverride: true,
        existingTicket
      };
    }

    if (intendedAction === 'scan_out' && !existingTicket) {
      return {
        isSuspicious: true,
        reason: 'This ticket cannot be scanned out before it has been scanned in.',
        confidence: 'high',
        allowOverride: false
      };
    }

    if (intendedAction === 'scan_out' && existingTicket && !existingTicket.isPresent) {
      return {
        isSuspicious: true,
        reason: 'This ticket has already been scanned out.',
        confidence: 'high',
        allowOverride: false,
        existingTicket
      };
    }

    if (!existingTicket) {
      return {
        isSuspicious: false,
        reason: 'New ticket - no previous record',
        confidence: 'low',
        allowOverride: true
      };
    }

    const timeDifference = newTimestamp - existingTicket.timestamp;
    if (intendedAction === 'scan_out' && timeDifference < minimumExitIntervalMs) {
      return {
        isSuspicious: true,
        reason: `Exit must be at least 15 seconds after entry. Only ${Math.max(0, Math.floor(timeDifference / 1000))} seconds have passed.`,
        confidence: 'high',
        allowOverride: false,
        existingTicket
      };
    }

    return {
      isSuspicious: false,
      reason: 'Normal ticket usage pattern',
      confidence: 'low',
      allowOverride: true,
      existingTicket
    };
  }

  /**
   * Compare local ticket count with peer-reported count and trigger sync if needed
   * @param eventId The event ID to compare
   * @param peerCount The count reported by peer
   * @param peerDeviceId The ID of the reporting peer
   * @returns Promise that resolves with comparison results
   */
  public async compareCountWithPeer(eventId: string, peerCount: number, peerDeviceId: string): Promise<{
    needsSync: boolean;
    localCount: number;
    peerCount: number;
    deficit: number;
    action: 'none' | 'request_tickets' | 'share_tickets';
  }> {
    const localTickets = await this.getTicketsForEvent(eventId);
    const localPresentCount = localTickets.filter(t => t.isPresent).length;
    const deficit = peerCount - localPresentCount;

    this.logger.log('Attendance', '📊 Comparing counts with peer', {
      eventId,
      localCount: localPresentCount,
      peerCount,
      deficit,
      peerDeviceId
    });

    let action: 'none' | 'request_tickets' | 'share_tickets' = 'none';
    let needsSync = false;

    if (deficit > 0) {
      // Peer has more tickets - we might be missing some
      needsSync = true;
      action = 'request_tickets';
      this.logger.log('Attendance', '⬇️ Local count is lower - requesting ticket sync', {
        deficit,
        peerDeviceId
      });
    } else if (deficit < 0) {
      // We have more tickets - peer might be missing some
      needsSync = true;
      action = 'share_tickets';
      this.logger.log('Attendance', '⬆️ Local count is higher - offering ticket sync', {
        surplus: Math.abs(deficit),
        peerDeviceId
      });
    }

    if (needsSync && action === 'request_tickets') {
      await this.requestTicketSync(eventId, peerCount, localPresentCount);
    }

    return {
      needsSync,
      localCount: localPresentCount,
      peerCount,
      deficit,
      action
    };
  }

  // NOTE: Event management methods removed to eliminate duplication with EventService
  // Events should be managed by EventService only - this service focuses on attendance/tickets

  private async getDb(): Promise<IDBDatabase> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    return this.db;
  }

  /**
   * Handle incoming ticket data from a peer
   * @param tickets Array of ticket records from a peer
   * @param eventId The event ID these tickets belong to
   * @returns Promise that resolves when tickets are processed
   */
  public async handleSharedTickets(tickets: TicketRecord[], eventId: string): Promise<{
    processed: number,
    duplicates: number,
    errors: number
  }> {
    if (!this.db) {
      throw new Error('Database not initialized');
    }

    const stats = {
      processed: 0,
      duplicates: 0,
      errors: 0
    };

    this.logger.log('Attendance', `Processing ${tickets.length} shared tickets for event ${eventId}`);

    // Process each ticket
    for (const sharedTicket of tickets) {
      try {
        // Only process tickets for the specified event
        if (sharedTicket.eventId !== eventId) {
          this.logger.warn('Attendance', 'Received ticket for different event', {
            ticketId: sharedTicket.ticketId,
            sharedEventId: sharedTicket.eventId,
            currentEventId: eventId
          });
          continue;
        }

        // Check if we already have this ticket
        const existingTicket = await this.getTicketFromDb(sharedTicket.ticketId);

        if (existingTicket) {
          // If the existing ticket is newer, keep it
          if (existingTicket.timestamp >= sharedTicket.timestamp) {
            stats.duplicates++;
            this.logger.debug('Attendance', 'Ignoring older shared ticket', {
              ticketId: sharedTicket.ticketId,
              sharedTimestamp: new Date(sharedTicket.timestamp).toISOString(),
              localTimestamp: new Date(existingTicket.timestamp).toISOString(),
              timeDifference: existingTicket.timestamp - sharedTicket.timestamp
            });
            continue;
          }

          // If shared ticket is newer, update local database
          this.logger.log('Attendance', 'Updating ticket with newer data from peer', {
            ticketId: sharedTicket.ticketId,
            previousState: existingTicket.isPresent ? 'Present' : 'Exited',
            newState: sharedTicket.isPresent ? 'Present' : 'Exited'
          });

          // Update the ticket without triggering a broadcast to avoid loops
          await this.saveTicketToDbInternal({
            ...sharedTicket,
            syncSource: 'remote'
          });
          stats.processed++;

          // Update local counts if present state changed
          if (existingTicket.isPresent !== sharedTicket.isPresent) {
            const currentCount = this.countSignal();
            const newCount = sharedTicket.isPresent ? currentCount + 1 : Math.max(0, currentCount - 1);

            this.countSignal.set(newCount);
            await this.storage.set(`count_${eventId}`, newCount.toString());

            this.logger.log('Attendance', 'Updated count due to shared ticket state change', {
              ticketId: sharedTicket.ticketId,
              oldCount: currentCount,
              newCount
            });
          }
        } else {
          // This is a new ticket we haven't seen before
          this.logger.log('Attendance', 'Adding new ticket from peer', {
            ticketId: sharedTicket.ticketId,
            state: sharedTicket.isPresent ? 'Present' : 'Exited',
            eventId
          });

          // Save the new ticket without triggering a broadcast
          await this.saveTicketToDbInternal({
            ...sharedTicket,
            syncSource: 'remote'
          });
          stats.processed++;

          // Update count if the person is present
          if (sharedTicket.isPresent) {
            const currentCount = this.countSignal();
            const newCount = currentCount + 1;

            this.countSignal.set(newCount);
            await this.storage.set(`count_${eventId}`, newCount.toString());

            this.logger.log('Attendance', 'Increased count for new shared ticket', {
              ticketId: sharedTicket.ticketId,
              oldCount: currentCount,
              newCount
            });
          }
        }
      } catch (error) {
        stats.errors++;
        this.logger.error('Attendance', `Error processing shared ticket ${sharedTicket.ticketId}`, error);
      }
    }

    this.logger.log('Attendance', 'Finished processing shared tickets', stats);
    return stats;
  }

  /**
   * Ensure the database is initialized
   * This can be called to make sure IndexedDB is ready before performing operations
   */
  public async ensureDatabaseInitialized(): Promise<void> {
    if (this.db) {
      this.logger.log('Attendance', 'Database already initialized');
      return;
    }

    this.logger.log('Attendance', 'Ensuring database is initialized');
    return new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

        request.onerror = () => {
          this.logger.error('Attendance', 'Database error during initialization', request.error);
          reject(request.error);
        };

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(this.TICKET_STORE)) {
            const store = db.createObjectStore(this.TICKET_STORE, { keyPath: 'ticketId' });
            store.createIndex('eventId', 'eventId', { unique: false });
            store.createIndex('isPresent', 'isPresent', { unique: false });
            this.logger.log('Attendance', 'Created ticket store');
          }
        };

        request.onsuccess = (event) => {
          this.db = (event.target as IDBOpenDBRequest).result;
          this.logger.log('Attendance', 'Database initialized');
          resolve();
        };
      } catch (error) {
        this.logger.error('Attendance', 'Failed to initialize database', error);
        reject(error);
      }
    });
  }

  /**
   * Initialize the count for a new event to 0 in storage and update the count signal
   */
  public async setInitialCount(eventId: string): Promise<void> {
    try {
      const key = this.getStorageKey(eventId);
      await this.storage.set(key, '0');
      this.countSignal.set(0);
    } catch (error) {
      console.error('[Attendance] Failed to initialize count for new event:', error);
    }
  }

  public async clearCountsForEvent(eventId: string): Promise<void> {
    this.logger.log('Attendance', `🧹 Clearing counts for event: ${eventId}`);

    // Clear in-memory signals if this is the current event
    if (this.currentEventId() === eventId) {
      this.countSignal.set(0);
      this.deviceCountSignal.set(0);
    }

    // Remove from storage
    await this.storage.remove(this.getStorageKey(eventId));
    await this.storage.remove(this.getDeviceCountKey(eventId));

    // Also ensure IndexedDB tickets are cleared (though deleteTicketsForEvent handles this)
    // if (this.db) {
    //   const transaction = this.db.transaction(this.TICKET_STORE, 'readwrite');
    //   const store = transaction.objectStore(this.TICKET_STORE);
    //   const request = store.openCursor();

    //   request.onsuccess = (event) => {
    //     const cursor = (event.target as IDBRequest).result;
    //     if (cursor) {
    //       if (cursor.value.eventId === eventId) {
    //         cursor.delete();
    //       }
    //       cursor.continue();
    //     }
    //   };

    //   await new Promise(resolve => transaction.oncomplete = resolve);
    // }

    this.logger.log('Attendance', `✅ Cleared counts for event ${eventId}`);
  }

  /**
   * Notify all connected peers that server count was updated
   * This triggers them to refresh their count from the server
   */
  private async notifyPeersOfServerUpdate(eventId: string, serverCount: number): Promise<void> {
    try {
      // Lazy load WebRTC service to avoid circular dependency
      if (!this.webrtcMeshService) {
        const { inject } = await import('@angular/core');
        const { WebrtcMeshService } = await import('./webrtc-mesh.service');
        this.webrtcMeshService = inject(WebrtcMeshService);
      }

      // Broadcast to all peers in the mesh
      this.webrtcMeshService.broadcastDataMessage({
        type: 'server-count-updated',
        eventId: eventId,
        serverCount: serverCount,
        timestamp: Date.now()
      }, 'system');

      this.logger.log('Attendance', `Notified peers of server count update: ${serverCount}`);
    } catch (error) {
      this.logger.error('Attendance', 'Failed to notify peers of server update:', error);
      // Non-critical - peers will sync on their own eventually
    }
  }

  /**
   * Reconcile local tickets with server - sync any missing or mismatched tickets
   * This ensures the server has all tickets and correct isPresent status
   */
  public async reconcileWithServer(eventId: string, eventPassword?: string, isCreator?: boolean): Promise<{ synced: number; conflicts: number }> {
    this.logger.log('Attendance', `🔄 Starting ticket reconciliation with server for event ${eventId}`);
    
    try {
      // Only upload tickets that this device originally scanned.
      // Tickets received via mesh have a different originDeviceId and
      // will be uploaded by the device that scanned them.
      const localDeviceId = this.deviceService.deviceId();
      const localTickets = (await this.getAllTicketsFromDb(eventId))
        .filter(ticket => ticket.originDeviceId === localDeviceId);
      this.logger.log('Attendance', `📱 Found ${localTickets.length} locally-scanned tickets (originDeviceId=${localDeviceId})`);

      if (localTickets.length === 0) {
        this.logger.log('Attendance', '✅ No local tickets to reconcile');
        return { synced: 0, conflicts: 0 };
      }

      // 2. Get all server tickets
      const serverResponse = await this.serverSyncService.getEventTickets(eventId);
      const serverTickets = serverResponse.tickets || [];
      this.logger.log('Attendance', `🌐 Server has ${serverTickets.length} tickets`);

      // 3. Find missing and mismatched tickets
      const ticketsToSync: any[] = [];
      
      for (const localTicket of localTickets) {
        const serverTicket = serverTickets.find(st => st.ticketId === localTicket.ticketId);
        
        if (!serverTicket) {
          // Ticket missing from server entirely
          this.logger.log('Attendance', `❗ Ticket missing from server: ${localTicket.ticketId}`);
          ticketsToSync.push({
            ticketId: localTicket.ticketId,
            action: localTicket.isPresent ? 'scan_in' : 'scan_out',
            timestamp: localTicket.timestamp
          });
        } else {
          // Check status mismatch - local version is source of truth
          const serverIsPresent = serverTicket.status === 'present';
          if (serverIsPresent !== localTicket.isPresent) {
            this.logger.log('Attendance', `⚠️ Status mismatch for ${localTicket.ticketId}: local=${localTicket.isPresent}, server=${serverIsPresent}`);
            ticketsToSync.push({
              ticketId: localTicket.ticketId,
              action: localTicket.isPresent ? 'scan_in' : 'scan_out',
              timestamp: localTicket.timestamp
            });
          }
        }
      }

      // 4. Sync missing/mismatched tickets to server
      if (ticketsToSync.length > 0) {
        this.logger.log('Attendance', `📤 Syncing ${ticketsToSync.length} tickets to server`);
        const localPresentCount = await this.getTotalScanned(eventId);
        const syncResponse = await this.serverSyncService.syncTicketScans(eventId, ticketsToSync, localPresentCount, undefined, eventPassword, isCreator, false);
        this.logger.log('Attendance', `✅ Reconciliation complete: ${syncResponse.processed} synced, ${syncResponse.conflicts.length} conflicts`);
        return { synced: syncResponse.processed, conflicts: syncResponse.conflicts.length };
      } else {
        this.logger.log('Attendance', '✅ All tickets in sync with server');
        return { synced: 0, conflicts: 0 };
      }
    } catch (error) {
      this.logger.error('Attendance', '❌ Reconciliation failed:', error);
      return { synced: 0, conflicts: 0 };
    }
  }

  /**
   * Get all tickets for an event from IndexedDB
   */
  private async getAllTicketsFromDb(eventId: string): Promise<TicketRecord[]> {
    if (!this.db) {
      await this.initDb();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.TICKET_STORE], 'readonly');
      const store = transaction.objectStore(this.TICKET_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        const allTickets = request.result || [];
        // Filter by eventId
        const eventTickets = allTickets.filter((ticket: TicketRecord) => ticket.eventId === eventId);
        resolve(eventTickets);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }
}